import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { CreditCard, CheckCircle2, AlertTriangle, Users, ArrowRight, ExternalLink, RefreshCw } from 'lucide-react';
import { billingApi } from '../lib/api';
import { useToast } from '../hooks/use-toast';

const Billing = () => {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(false);

  const load = async () => {
    try {
      const r = await billingApi.summary();
      setSummary(r.data);
    } catch (e) {
      toast({ title: 'Error', description: 'Could not load billing info' });
    }
  };

  useEffect(() => { load(); }, []);

  // Poll Stripe checkout return
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) return;
    setPolling(true);
    let attempts = 0;
    const maxAttempts = 8;
    const poll = async () => {
      try {
        const r = await billingApi.checkoutStatus(sessionId);
        if (r.data.payment_status === 'paid') {
          toast({ title: 'Payment method added', description: 'Your subscription is active.' });
          setPolling(false);
          searchParams.delete('session_id');
          setSearchParams(searchParams, { replace: true });
          load();
          return;
        }
        if (r.data.status === 'expired') {
          toast({ title: 'Session expired', description: 'Please try again.' });
          setPolling(false);
          return;
        }
        if (++attempts >= maxAttempts) {
          setPolling(false);
          toast({ title: 'Still processing', description: 'Refresh in a moment.' });
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setPolling(false);
      }
    };
    poll();
  }, [searchParams]);

  const startCheckout = async () => {
    setLoading(true);
    try {
      const r = await billingApi.checkout();
      window.location.href = r.data.url;
    } catch (e) {
      toast({ title: 'Could not start checkout', description: e.response?.data?.detail || 'Try again.' });
      setLoading(false);
    }
  };

  const openPortal = async () => {
    try {
      const r = await billingApi.portal();
      window.location.href = r.data.url;
    } catch (e) {
      toast({ title: 'Portal unavailable', description: e.response?.data?.detail || 'Try again.' });
    }
  };

  const fmtMoney = (cents) => `$${(cents / 100).toFixed(2)}`;

  if (!summary) {
    return <div className="p-8 text-center text-stone-500">Loading billing...</div>;
  }

  const hasSubscription = summary.hasPaymentMethod && summary.subscriptionStatus;
  const statusActive = ['active', 'trialing'].includes(summary.subscriptionStatus);

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-widest text-[#4b5320] mb-2">Subscription</div>
        <h1 className="font-display text-4xl text-stone-900">Billing</h1>
        <p className="text-sm text-stone-600 mt-2">You're billed <span className="font-semibold text-stone-900">$6.99 per month, per active client</span> with at least one dispute on the portal.</p>
      </div>

      {polling && (
        <div className="mb-6 p-4 border border-[#4b5320]/40 bg-[#4b5320]/5 rounded-sm flex items-center gap-3">
          <RefreshCw className="w-5 h-5 text-[#4b5320] animate-spin" />
          <div className="text-sm text-stone-800">Confirming payment with Stripe...</div>
        </div>
      )}

      {/* Current bill */}
      <div className="border border-stone-200/80 bg-white rounded-sm p-8 mb-6 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-[#4b5320] to-transparent"></div>
        <div className="text-xs uppercase tracking-widest text-[#4b5320] mb-2">Current Month</div>
        <h2 className="font-display text-2xl text-stone-900 mb-6">Usage & Charge</h2>

        <div className="grid md:grid-cols-3 gap-6">
          <div className="border-l-2 border-[#4b5320] pl-4">
            <div className="text-xs uppercase tracking-widest text-stone-500">Billable clients</div>
            <div className="font-display text-4xl text-stone-900 mt-1">{summary.billableClients}</div>
            <div className="text-xs text-stone-500 mt-1">of {summary.totalClients} total</div>
          </div>
          <div className="border-l-2 border-stone-300 pl-4">
            <div className="text-xs uppercase tracking-widest text-stone-500">Per client</div>
            <div className="font-display text-4xl text-stone-900 mt-1">{fmtMoney(summary.perClientCents)}</div>
            <div className="text-xs text-stone-500 mt-1">monthly recurring</div>
          </div>
          <div className="border-l-2 border-stone-300 pl-4">
            <div className="text-xs uppercase tracking-widest text-stone-500">This month</div>
            <div className="font-display text-4xl text-[#4b5320] mt-1">{fmtMoney(summary.monthlyCents)}</div>
            <div className="text-xs text-stone-500 mt-1">{summary.billableClients} × $6.99</div>
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-stone-200">
          <div className="text-xs uppercase tracking-widest text-stone-500 mb-2">How billing works</div>
          <ul className="text-sm text-stone-700 space-y-1.5">
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#4b5320] mt-0.5 flex-shrink-0" />
              You're charged the moment a client gets their first dispute letter — not before.
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#4b5320] mt-0.5 flex-shrink-0" />
              Your subscription auto-adjusts as you add billable clients (prorated).
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#4b5320] mt-0.5 flex-shrink-0" />
              Cancel anytime. No long-term contract.
            </li>
          </ul>
        </div>
      </div>

      {/* Status / Action */}
      {!hasSubscription ? (
        <div className="border border-amber-300 bg-amber-50 rounded-sm p-8 mb-6">
          <div className="flex items-start gap-4">
            <AlertTriangle className="w-6 h-6 text-amber-700 mt-1 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-display text-xl text-stone-900 mb-2">No payment method on file</h3>
              <p className="text-sm text-stone-700 mb-5">
                You can register and add clients for free. Once you generate the first dispute for any client,
                a payment method is required so we can begin the $6.99/month subscription for that client.
              </p>
              <Button onClick={startCheckout} disabled={loading} className="bg-[#4b5320] hover:bg-[#3d4419] text-white font-semibold rounded-sm">
                <CreditCard className="w-4 h-4 mr-2" />
                {loading ? 'Redirecting to Stripe...' : 'Add payment method'} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="border border-stone-200/80 bg-white rounded-sm p-8">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-[#4b5320] mb-2">Subscription</div>
              <h3 className="font-display text-2xl text-stone-900 mb-1">Active</h3>
              <div className="text-sm text-stone-600">
                Status: <span className={`font-medium ${statusActive ? 'text-[#4b5320]' : 'text-amber-700'}`}>{summary.subscriptionStatus}</span>
                {' · '}Subscription quantity: <span className="font-mono text-stone-900">{summary.subscriptionQuantity}</span>
              </div>
            </div>
            <Button onClick={openPortal} variant="outline" className="border-stone-300 bg-white text-stone-700 hover:bg-stone-100 rounded-sm">
              Manage in Stripe Portal <ExternalLink className="w-4 h-4 ml-2" />
            </Button>
          </div>
          {summary.subscriptionQuantity !== summary.billableClients && summary.billableClients > 0 && (
            <div className="mt-4 p-3 border border-amber-300 bg-amber-50 rounded-sm text-sm text-stone-700">
              <AlertTriangle className="w-4 h-4 text-amber-700 inline mr-2" />
              Subscription quantity ({summary.subscriptionQuantity}) doesn't match billable clients ({summary.billableClients}). It will sync on the next dispute action.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Billing;
